import { cloneProductApi, createProductApi, deleteProductApi, getProductApi, getProductsApi, stopProductApi, updateProductApi } from '@app/api/product.api';
import { ProductDataArray, ProductItem, ProductQueryParams, ProductModel } from '@app/interfaces/product';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface ProductState {
  data: ProductModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: ProductItem[];
  loading: boolean;
  error: string | null;
  record: ProductModel | null;
}

const initialState: ProductState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  record: null,
};

interface ResponseData {
  data: ProductItem;
  message: string;
}

export const getProducts = createAsyncThunk<ProductDataArray, ProductQueryParams>(
  'product/getProducts',
  async (params: ProductQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getProductsApi(params)) as ProductDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getProduct = createAsyncThunk<ProductModel, number>(
  'product/getProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const data = await getProductApi(productId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const createProduct = createAsyncThunk<ResponseData, ProductModel>(
  'product/createCustomer', async (data) => {
  const response = await createProductApi(data, {
    headers: { 'Content-Type': 'multipart/form-data'}
  });
  return response;
});

export const updateProduct = createAsyncThunk<ResponseData, { productId: number; formData: ProductModel }>(
  'product/updateProduct',
  async ({ productId, formData }) => {
    const response = await updateProductApi(productId, formData, {
      headers: { 'Content-Type': 'multipart/form-data'}
    });
    return response;
  },
);

export const deleteProduct = createAsyncThunk<{ productId: number; response: any }, number>(
  'product/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await deleteProductApi(productId);
      return { productId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const stopProduct = createAsyncThunk<ResponseData, number>(
  'product/stopProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await stopProductApi(productId);
      return response;

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const cloneProduct = createAsyncThunk<ResponseData, number>(
  'product/cloneProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await cloneProductApi(productId);
      return response;
      
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      // Chỉ ghi đè field được truyền vào (tránh xoá nhầm khi dispatch một phần).
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        console.log('Fulfilled payload:', action.payload);
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: ProductItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        const product = (action.payload as any).data;
        state.record = product.attributes;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(stopProduct.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(cloneProduct.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const { productId, response } = action.payload;
        state.data = state.data.filter((product) => Number(product.id) !== productId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = productSlice.actions;

export default productSlice.reducer;
